import { Factory, SquaresFour, Yarn } from "@phosphor-icons/react"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import { z } from "zod"
import { useIncomingOrdersStatuses } from "~/hooks/useIncomingOrdersFilters"
import { IncomingOrderStatus } from "~/constants/incomingOrdersStatus"
import {
  useCollectionGroups,
  useGetCollectionsWithDetails,
  type ICollectionGroup,
} from "~/hooks/useCollections"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import { useMemo } from "react"
import { api } from "~/utils/api"
import { useFilteredVariants } from "~/hooks/useVariants"
import { conflictingWarehouseClaims } from "~/utils/fashionBuySettings"
import { useGetPurchaseOrders } from "~/hooks/usePurchaseOrders"
import { isCommittedDraftStatus } from "~/utils/purchaseOrders"
import { sumBy, intersection } from "lodash"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { Module } from "~/components/design-system/pages"
import {
  CollectionDetailProvider,
  type CollectionDetailContextValue,
} from "~/context/CollectionDetailProvider"
import { useCompany } from "~/hooks/useCompany"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"

const searchSchema = z.object({
  isGroup: z.boolean().optional().default(false),
})

export const Route = createFileRoute(
  "/_auth/procurement/supply-plan/collection/$collectionId/_layout",
)({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
})

function RouteComponent() {
  const { collectionId } = Route.useParams()
  const { data: company } = useCompany()
  const { isGroup } = Route.useSearch()
  const { data: rawCollections, isLoading: collectionDetailsLoading } =
    useGetCollectionsWithDetails({ filterOutVariants: true, preferDemandPlan: true })
  const { data: collectionGroups, isLoading: isCollectionGroupsLoading } = useCollectionGroups()
  const { data: purchasePlans, isLoading: isPurchasePlansLoading } =
    api.purchasePlan.getAll.useQuery()
  const { data: purchaseOrders, isLoading: purchaseOrdersLoading } = useGetPurchaseOrders()
  const allVariants = useFilteredVariants()
  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: hasExternalCollectionId } = api.purchaseOrder.hasExternalCollectionId.useQuery()
  const { data: hasShipmentMethod } = api.purchaseOrder.hasShipmentMethod.useQuery()
  const { data: hasVendorId } = api.purchaseOrder.hasVendorId.useQuery()

  const { collectionGroup, collections } = useMemo(() => {
    let cols: typeof rawCollections = []
    let group: ICollectionGroup | undefined

    if (isGroup) {
      group = collectionGroups?.find((cg) => cg.id === Number(collectionId))
      cols = rawCollections?.filter((c) => c.collectionGroupId === group?.id) ?? []
    } else {
      cols = rawCollections?.filter((c) => c.id === Number(collectionId)) ?? []
    }

    return {
      collectionGroup: group,
      collections: cols,
    }
  }, [isGroup, collectionGroups, collectionId, rawCollections])

  const { data: incomingOrders } = api.incomingOrders.getIncomingOrdersByCollections.useQuery(
    {
      collectionIds: collections.map((col) => col.id),
    },
    { enabled: collections.length > 0 },
  )

  const { selectedStatuses } = useIncomingOrdersStatuses()
  const showArrived = selectedStatuses.includes(IncomingOrderStatus.Arrived)

  const { data: detailIncomingMap, isLoading: detailIncomingLoading } =
    api.variantTableData.incomingOrderDetailsMap.useQuery()
  const { data: detailArrivedMap, isLoading: detailArrivedLoading } =
    api.variantTableData.incomingOrderDetailsMap.useQuery(
      { status: "arrived" },
      { enabled: showArrived },
    )

  const incomingOrdersLoading = detailIncomingLoading || (showArrived && detailArrivedLoading)

  const relevantCollectionIds = useMemo(() => {
    return new Set(collections.map((c) => c.id.toString()))
  }, [collections])

  const filteredPurchasePlans = useMemo(() => {
    if (!purchasePlans) return []

    return purchasePlans.filter((plan) => {
      if (!plan.collectionIds || plan.collectionIds.length === 0) return false
      return plan.collectionIds.some((colId) => relevantCollectionIds.has(colId))
    })
  }, [purchasePlans, relevantCollectionIds])

  const tabs: HeaderTab[] = [
    {
      label: "Overview",
      icon: <SquaresFour />,
      path: "/procurement/supply-plan/collection/$collectionId/overview" as const,
    },
    {
      label: "Capacity Planning",
      icon: <Factory />,
      path: "/procurement/supply-plan/collection/$collectionId/capacity" as const,
    },
    {
      label: "Greige",
      icon: <Yarn />,
      disabled: filteredPurchasePlans.length === 0,
      tooltip:
        filteredPurchasePlans.length === 0 ? "Create a buy to access greige planning" : undefined,
      path: "/procurement/supply-plan/collection/$collectionId/greige" as const,
    },
  ]

  // A collection stays offerable while ANY of its warehouses is unbought. Buys used to be exclusive
  // per collection — one active buy took it off the list outright — but a buy scoped to one
  // warehouse (PRO-1126) leaves the rest to be bought separately, so exclusivity is per
  // (collection, warehouse) now. The dialog then disables the warehouses that are taken.
  const collectionsWithFreeWarehouses = useMemo(() => {
    const primaryWarehouseIds = (warehouses ?? []).filter((w) => w.isPrimary).map((w) => w.id)
    if (primaryWarehouseIds.length === 0) return collections
    return collections.filter((collection) => {
      const blocked = conflictingWarehouseClaims({
        plans: filteredPurchasePlans,
        primaryWarehouseIds,
        collectionIds: [`${collection.id}`],
      })
      return blocked.size < primaryWarehouseIds.length
    })
  }, [filteredPurchasePlans, collections, warehouses])

  const collectionIdsArray = useMemo(() => {
    return Array.from(relevantCollectionIds)
  }, [relevantCollectionIds])

  const filteredVariantsForIncomingOrders = useMemo(() => {
    const useExplicitMapping = company?.incomingHasCorrectCollectionId ?? true
    const relevantExternalCollectionIds = new Set(
      collections.map((c) => c.externalId).filter((id): id is string => id !== null),
    )

    if (useExplicitMapping) {
      if (relevantExternalCollectionIds.size === 0) return []

      return allVariants
        .map((variant) => {
          const incoming = detailIncomingMap?.get(variant.variantId) ?? []
          const arrived = showArrived ? (detailArrivedMap?.get(variant.variantId) ?? []) : []
          const allOrders = [...incoming, ...arrived].filter(
            (order) =>
              order.externalCollectionId &&
              relevantExternalCollectionIds.has(order.externalCollectionId),
          )
          return { ...variant, incomingOrders: allOrders }
        })
        .filter((variant) => variant.incomingOrders.length > 0)
    }

    const relevantVariantIds = new Set(collections.flatMap((c) => c.variantIds))
    if (relevantVariantIds.size === 0) return []

    return allVariants
      .map((variant) => {
        if (!relevantVariantIds.has(variant.variantId)) return null
        const incoming = detailIncomingMap?.get(variant.variantId) ?? []
        const arrived = showArrived ? (detailArrivedMap?.get(variant.variantId) ?? []) : []
        const allOrders = [...incoming, ...arrived]
        return { ...variant, incomingOrders: allOrders }
      })
      .filter(
        (variant): variant is NonNullable<typeof variant> =>
          variant !== null && variant.incomingOrders.length > 0,
      )
  }, [
    allVariants,
    collections,
    company?.incomingHasCorrectCollectionId,
    detailIncomingMap,
    detailArrivedMap,
    showArrived,
  ])

  const hasMultipleWarehouses = useMemo(() => {
    return warehouses && warehouses.length > 1
  }, [warehouses])

  const totalIncomingOrderUnits = useMemo(() => {
    if (incomingOrders == undefined) return 0
    return sumBy(Array.from(incomingOrders.values()), (collection) => collection.quantity)
  }, [incomingOrders])

  const totalProjectedDemand = sumBy(collections, (col) => col.forecastedUnits)

  const incomingOrdersByCollection = useMemo(() => {
    const byCollection = new Map<number, number>()
    if (!incomingOrders) return byCollection

    for (const [collectionId, { quantity }] of incomingOrders) {
      byCollection.set(collectionId, quantity)
    }

    return byCollection
  }, [incomingOrders])

  const totalPurchaseOrderUnits = useMemo(() => {
    if (!purchaseOrders) return 0

    const filteredPOs = purchaseOrders.filter((order) => {
      if (isCommittedDraftStatus(order.status)) return false
      if (!order.collectionIds || order.collectionIds.length === 0) return false
      return intersection(order.collectionIds, collectionIdsArray).length > 0
    })

    return sumBy(filteredPOs, (order) => sumBy(order.items, "quantity"))
  }, [purchaseOrders, collectionIdsArray])

  const purchaseOrdersByCollection = useMemo(() => {
    const byCollection = new Map<number, number>()

    if (!purchaseOrders) return byCollection

    for (const order of purchaseOrders) {
      if (isCommittedDraftStatus(order.status)) continue
      if (!order.collectionIds || order.collectionIds.length === 0) continue

      const orderTotal = sumBy(order.items, "quantity")
      const relevantColIds = order.collectionIds.filter((id) => relevantCollectionIds.has(id))
      if (relevantColIds.length > 0) {
        const perCollection = orderTotal / relevantColIds.length
        for (const colIdStr of relevantColIds) {
          const colId = Number(colIdStr)
          const current = byCollection.get(colId) ?? 0
          byCollection.set(colId, current + perCollection)
        }
      }
    }

    return byCollection
  }, [purchaseOrders, relevantCollectionIds])

  const collectionBreakdownData = useMemo(() => {
    return collections.map((col) => {
      const forecast = col.forecastedUnits ?? 0
      const incomingOrders = incomingOrdersByCollection.get(col.id) ?? 0
      const poUnits = purchaseOrdersByCollection.get(col.id) ?? 0
      const incoming = incomingOrders + poUnits
      const leftToBuy = Math.max(0, forecast - incoming)

      return {
        collectionId: col.id,
        collectionName: col.name,
        forecastedDemand: Math.round(forecast),
        incoming: Math.round(incoming),
        leftToBuy: Math.round(leftToBuy),
      }
    })
  }, [collections, incomingOrdersByCollection, purchaseOrdersByCollection])

  const recentLabel = isGroup ? collectionGroup?.name : collections[0]?.name
  useRecordRecentlyViewed(
    collections.length > 0 && recentLabel
      ? {
          type: "supply-plan",
          route: `/procurement/supply-plan/collection/${collectionId}${isGroup ? "?isGroup=true" : ""}`,
          label: recentLabel,
        }
      : null,
  )

  if (collectionDetailsLoading || isCollectionGroupsLoading || isPurchasePlansLoading) {
    return <LoadingOverlay />
  }

  if (collections.length === 0) {
    return <NotFound />
  }

  const displayName = isGroup
    ? (collectionGroup?.name ?? "Collection Group")
    : (collections[0]?.name ?? "Collection")

  const showCollectionBreakdown = isGroup && collections.length > 1

  const contextValue: CollectionDetailContextValue = {
    collectionId: Number(collectionId),
    collections,
    collectionGroup,
    isGroup,
    filteredPurchasePlans,
    collectionsWithFreeWarehouses,
    collectionIdsArray,
    collectionBreakdownData,
    showCollectionBreakdown,
    totalProjectedDemand,
    totalIncomingOrderUnits,
    totalPurchaseOrderUnits,
    purchaseOrdersLoading,
    collectionDetailsLoading,
    incomingOrdersLoading,
    filteredVariantsForIncomingOrders,
    hasExternalCollectionId: hasExternalCollectionId ?? false,
    hasShipmentMethod: hasShipmentMethod ?? false,
    hasMultipleWarehouses: hasMultipleWarehouses ?? false,
    hasVendorId: hasVendorId ?? false,
  }

  return (
    <CollectionDetailProvider value={contextValue}>
      <Module
        breadcrumb={
          <BreadcrumbWrapper
            items={[
              { label: "Procurement", href: "/procurement" },
              { label: "Supply Plan", href: "/procurement/supply-plan" },
              { label: displayName },
            ]}
          />
        }
        tabs={{ items: tabs, path: "/procurement/supply-plan/collection/$collectionId" }}
      >
        <Outlet />
      </Module>
    </CollectionDetailProvider>
  )
}
